import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Users, Clock, ArrowLeft } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PastEventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPastEvents();
  }, []);

  const fetchPastEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      // Filter only past events (end_date < today)
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const pastEvents = response.data.filter(event => !event.date_tbd && new Date(event.end_date) < now);
      // Sort by start_date descending (most recent first)
      const sorted = pastEvents.sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
      setEvents(sorted);
    } catch (error) {
      console.error('Error fetching past events:', error);
      toast.error('Errore nel caricamento degli eventi passati');
    } finally {
      setLoading(false);
    }
  };

  // Format date range for events
  const formatDateRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    // If same day, show only one date
    if (startDate === endDate) {
      return start.toLocaleDateString('it-IT', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric'
      });
    }
    
    // Different days - show range
    const startFormatted = start.toLocaleDateString('it-IT', { 
      day: 'numeric', 
      month: 'long'
    });
    const endFormatted = end.toLocaleDateString('it-IT', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric'
    });
    
    return `${startFormatted} - ${endFormatted}`;
  };

  const getEventColor = (type) => {
    const colors = {
      stage: 'bg-purple-500',
      lecture: 'bg-blue-500',
      online: 'bg-green-500',
      formazione_insegnanti: 'bg-cyan-500'
    };
    return colors[type] || 'bg-gray-500';
  };

  const getEventIcon = (type) => {
    return <Calendar className="w-5 h-5" />;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <section className="bg-gradient-to-r from-gray-800 to-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center text-white hover:text-gray-200 mb-6 transition-colors">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Torna alla Home
          </Link>
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-4">Eventi Passati</h1>
            <p className="text-xl text-gray-300">
              Scopri tutti gli eventi che si sono già svolti
            </p>
          </div>
        </div>
      </section>

      {/* Events Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
              <p className="mt-4 text-gray-600">Caricamento eventi passati...</p>
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">Nessun evento passato</h3>
              <p className="text-gray-500">Non ci sono ancora eventi conclusi</p>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  {events.length} {events.length === 1 ? 'Evento' : 'Eventi'}
                </h2>
                <p className="text-gray-600">Eventi già conclusi</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {events.map((event) => (
                  <Card key={event.id} className="overflow-hidden border-0 shadow-lg hover:shadow-xl transition-shadow">
                    <div className={`h-2 ${getEventColor(event.type)}`}></div>
                    
                    <CardHeader className="pb-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className={`p-2 rounded-lg text-white ${getEventColor(event.type)}`}>
                          {getEventIcon(event.type)}
                        </div>
                        <Badge variant="secondary" className="text-xs uppercase tracking-wide bg-gray-200">
                          {event.type === 'stage' ? 'Stage' :
                           event.type === 'lecture' ? 'Lecture' :
                           event.type === 'online' ? 'Online' :
                           event.type === 'formazione_insegnanti' ? 'Formazione Insegnanti' : 
                           event.type}
                        </Badge>
                      </div>
                      <CardTitle className="text-xl font-bold text-gray-900">
                        {event.title}
                      </CardTitle>
                      <CardDescription className="text-gray-600 line-clamp-2">
                        {event.description}
                      </CardDescription>
                    </CardHeader>
                    
                    <CardContent className="space-y-3">
                      <div className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                        <Calendar className="w-4 h-4 text-red-500" />
                        <span>{formatDateRange(event.start_date, event.end_date)}</span>
                      </div>
                      
                      {event.location && (
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <MapPin className="w-4 h-4 text-red-500" />
                          <span>{event.location}</span>
                        </div>
                      )}
                      
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Users className="w-4 h-4 text-red-500" />
                        <span>{event.enrolled_count}/{event.max_participants} iscritti</span>
                      </div>
                      
                      <div className="pt-3">
                        <Badge variant="outline" className="bg-gray-100 text-gray-700 border-gray-300">
                          Evento Concluso
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default PastEventsPage;
